import { getConfig, resetConfig, setConfig, subscribe } from './state';
import type { Config, DeepPartial, GlobalConfig, SpeciesConfig } from './state';
import type { Species } from './types';

export type GlobalNumKey = {
    [K in keyof GlobalConfig]: GlobalConfig[K] extends number ? K : never;
}[keyof GlobalConfig];

export type GlobalBoolKey = {
    [K in keyof GlobalConfig]: GlobalConfig[K] extends boolean ? K : never;
}[keyof GlobalConfig];

export type SpeciesNumKey = keyof SpeciesConfig;

export interface SliderSpec<K extends string> {
    key: K;
    label: string;
    min: number;
    max: number;
    step: number;
}

export const SPECIES_KEYS: readonly Species[] = ['minnow', 'tetra', 'bass', 'pike'];

export const GLOBAL_SLIDERS: readonly SliderSpec<GlobalNumKey>[] = [
    { key: 'aggression', label: 'aggression', min: 0, max: 3, step: 0.05 },
    { key: 'shoaling', label: 'shoaling', min: 0, max: 2, step: 0.05 },
    { key: 'predatorRatio', label: 'predator ratio', min: 1, max: 5, step: 0.1 },
    { key: 'preyRatio', label: 'prey ratio', min: 0.1, max: 1, step: 0.05 },
    { key: 'boundaryMargin', label: 'boundary margin', min: 0, max: 200, step: 5 },
    { key: 'boundaryForce', label: 'boundary force', min: 0, max: 10, step: 0.1 },
    { key: 'frameMs', label: 'frame ms', min: 8, max: 50, step: 1 },
    { key: 'slowmoFactor', label: 'slow-mo factor', min: 0.5, max: 3, step: 0.1 },
    { key: 'lookRangeMult', label: 'look range', min: 50, max: 300, step: 5 },
    { key: 'wanderJitter', label: 'wander jitter', min: 0, max: 0.5, step: 0.005 },
    { key: 'absorbRatio', label: 'absorb ratio', min: 1, max: 5, step: 0.1 },
];

export const GLOBAL_CHECKBOXES: readonly { key: GlobalBoolKey; label: string }[] = [
    { key: 'showBehavior', label: 'show behavior' },
    { key: 'lifecycleEnabled', label: 'lifecycle' },
    { key: 'censusEnabled', label: 'census' },
];

export const SPECIES_SLIDERS: readonly SliderSpec<SpeciesNumKey>[] = [
    { key: 'count', label: 'count', min: 0, max: 60, step: 1 },
    { key: 'sizeMult', label: 'size', min: 0.5, max: 2, step: 0.05 },
    { key: 'speedMult', label: 'speed', min: 0.5, max: 2, step: 0.05 },
    { key: 'aggression', label: 'aggression', min: 0, max: 3, step: 0.05 },
];

function applyGlobalNum(field: GlobalNumKey, value: number): void {
    const partial: Partial<GlobalConfig> = { [field]: value };
    setConfig({ global: partial });
}

function applyGlobalBool(field: GlobalBoolKey, value: boolean): void {
    const partial: Partial<GlobalConfig> = { [field]: value };
    setConfig({ global: partial });
}

function applySpeciesNum(species: Species, field: SpeciesNumKey, value: number): void {
    const speciesPartial: Partial<SpeciesConfig> = { [field]: value };
    const partial = { [species]: speciesPartial } as DeepPartial<Config['species']>;
    setConfig({ species: partial });
}

function makeSliderRow(
    doc: Document,
    spec: SliderSpec<string>,
    initial: number,
    dataset: Record<string, string>,
    onInput: (value: number) => void,
): HTMLElement {
    const dt = doc.createElement('dt');
    const labelEl = doc.createElement('label');
    labelEl.textContent = spec.label;
    dt.appendChild(labelEl);

    const dd = doc.createElement('dd');
    const input = doc.createElement('input');
    input.type = 'range';
    input.min = String(spec.min);
    input.max = String(spec.max);
    input.step = String(spec.step);
    input.value = String(initial);
    for (const [k, v] of Object.entries(dataset)) {
        input.dataset[k] = v;
    }
    const valueEl = doc.createElement('span');
    valueEl.className = 'menu-value';
    valueEl.textContent = String(initial);
    input.addEventListener('input', () => {
        valueEl.textContent = input.value;
        onInput(Number(input.value));
    });

    const id = `menu-${dataset.scope ?? 'g'}-${dataset.species ?? ''}-${spec.key}`;
    input.id = id;
    labelEl.htmlFor = id;

    dd.appendChild(input);
    dd.appendChild(valueEl);

    const wrapper = doc.createElement('div');
    wrapper.className = 'menu-row';
    wrapper.appendChild(dt);
    wrapper.appendChild(dd);
    return wrapper;
}

function makeCheckboxRow(
    doc: Document,
    label: string,
    initial: boolean,
    dataset: Record<string, string>,
    onChange: (value: boolean) => void,
): HTMLElement {
    const wrapper = doc.createElement('div');
    wrapper.className = 'menu-row menu-row--checkbox';

    const labelEl = doc.createElement('label');
    const input = doc.createElement('input');
    input.type = 'checkbox';
    input.checked = initial;
    for (const [k, v] of Object.entries(dataset)) {
        input.dataset[k] = v;
    }
    input.addEventListener('change', () => {
        onChange(input.checked);
    });
    labelEl.appendChild(input);
    labelEl.appendChild(doc.createTextNode(' ' + label));
    wrapper.appendChild(labelEl);
    return wrapper;
}

export interface BuildMenuOptions {
    onCopyShareUrl?: () => void;
    onExportSvg?: () => void;
}

export function buildMenu(cfg: Config, doc: Document, opts: BuildMenuOptions = {}): HTMLElement {
    const root = doc.createElement('div');
    root.id = 'menu';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'fish settings');

    const inner = doc.createElement('div');
    inner.className = 'menu-inner';
    root.appendChild(inner);

    const closeLink = doc.createElement('a');
    closeLink.id = 'menu-close';
    closeLink.href = '#';
    closeLink.textContent = '[ x close ]';
    closeLink.className = 'menu-close';
    inner.appendChild(closeLink);

    const globalSection = doc.createElement('section');
    globalSection.dataset.section = 'global';
    const globalHeading = doc.createElement('h2');
    globalHeading.className = 'menu-heading';
    globalHeading.textContent = '--- global ---';
    globalSection.appendChild(globalHeading);

    for (const spec of GLOBAL_SLIDERS) {
        const row = makeSliderRow(
            doc,
            spec,
            cfg.global[spec.key],
            { scope: 'global', field: spec.key },
            (v) => {
                applyGlobalNum(spec.key, v);
            },
        );
        globalSection.appendChild(row);
    }

    for (const cb of GLOBAL_CHECKBOXES) {
        const row = makeCheckboxRow(
            doc,
            cb.label,
            cfg.global[cb.key],
            { scope: 'global', field: cb.key },
            (v) => {
                applyGlobalBool(cb.key, v);
            },
        );
        globalSection.appendChild(row);
    }

    inner.appendChild(globalSection);

    for (const sp of SPECIES_KEYS) {
        const section = doc.createElement('section');
        section.dataset.section = sp;

        const heading = doc.createElement('h2');
        heading.className = 'menu-heading';
        heading.textContent = `--- ${sp} ---`;
        section.appendChild(heading);

        for (const spec of SPECIES_SLIDERS) {
            const row = makeSliderRow(
                doc,
                spec,
                cfg.species[sp][spec.key],
                { scope: 'species', species: sp, field: spec.key },
                (v) => {
                    applySpeciesNum(sp, spec.key, v);
                },
            );
            section.appendChild(row);
        }
        inner.appendChild(section);
    }

    const wallsSection = doc.createElement('section');
    wallsSection.dataset.section = 'walls';
    const wallsHeading = doc.createElement('h2');
    wallsHeading.className = 'menu-heading';
    wallsHeading.textContent = '--- walls ---';
    wallsSection.appendChild(wallsHeading);

    const clearWallsLink = doc.createElement('a');
    clearWallsLink.id = 'menu-clear-walls';
    clearWallsLink.href = '#';
    clearWallsLink.textContent = '[ clear walls ]';
    clearWallsLink.className = 'menu-clear-walls';
    wallsSection.appendChild(clearWallsLink);

    const wallsHint = doc.createElement('p');
    wallsHint.className = 'menu-hint';
    wallsHint.textContent = 'hold P + drag to draw';
    wallsSection.appendChild(wallsHint);

    inner.appendChild(wallsSection);

    const shareSection = doc.createElement('section');
    shareSection.dataset.section = 'share';
    const shareHeading = doc.createElement('h2');
    shareHeading.className = 'menu-heading';
    shareHeading.textContent = '--- share ---';
    shareSection.appendChild(shareHeading);

    const shareLink = doc.createElement('a');
    shareLink.id = 'menu-copy-share';
    shareLink.href = '#';
    shareLink.textContent = '[ copy share url ]';
    shareLink.className = 'menu-copy-share';
    shareSection.appendChild(shareLink);

    const exportLink = doc.createElement('a');
    exportLink.id = 'menu-export-svg';
    exportLink.href = '#';
    exportLink.textContent = '[ export svg ]';
    exportLink.className = 'menu-export-svg';
    shareSection.appendChild(exportLink);
    inner.appendChild(shareSection);

    shareLink.addEventListener('click', (e) => {
        e.preventDefault();
        opts.onCopyShareUrl?.();
    });

    exportLink.addEventListener('click', (e) => {
        e.preventDefault();
        opts.onExportSvg?.();
    });

    const resetLink = doc.createElement('a');
    resetLink.id = 'menu-reset';
    resetLink.href = '#';
    resetLink.textContent = '[ reset ]';
    resetLink.className = 'menu-reset';
    inner.appendChild(resetLink);

    closeLink.addEventListener('click', (e) => {
        e.preventDefault();
        root.hidden = true;
    });

    resetLink.addEventListener('click', (e) => {
        e.preventDefault();
        resetConfig();
    });

    clearWallsLink.addEventListener('click', (e) => {
        e.preventDefault();
        setConfig({ walls: [] });
    });

    return root;
}

export function syncMenu(root: HTMLElement, cfg: Config): void {
    const inputs = root.querySelectorAll<HTMLInputElement>('input[data-scope]');
    for (const input of inputs) {
        const scope = input.dataset.scope;
        const field = input.dataset.field;
        if (field === undefined || field === '') continue;

        if (scope === 'global') {
            const v = (cfg.global as unknown as Record<string, unknown>)[field];
            if (input.type === 'checkbox') {
                if (typeof v === 'boolean' && input.checked !== v) {
                    input.checked = v;
                }
            } else if (typeof v === 'number') {
                const next = String(v);
                if (input.value !== next) input.value = next;
                const valueEl = input.nextElementSibling;
                if (valueEl instanceof HTMLElement && valueEl.classList.contains('menu-value')) {
                    valueEl.textContent = next;
                }
            }
        } else if (scope === 'species') {
            const sp = input.dataset.species as Species | undefined;
            if (sp === undefined) continue;
            const speciesCfg = cfg.species[sp] as unknown as Record<string, unknown>;
            const v = speciesCfg[field];
            if (typeof v === 'number') {
                const next = String(v);
                if (input.value !== next) input.value = next;
                const valueEl = input.nextElementSibling;
                if (valueEl instanceof HTMLElement && valueEl.classList.contains('menu-value')) {
                    valueEl.textContent = next;
                }
            }
        }
    }
}

export interface MenuHandle {
    root: HTMLElement;
    open: () => void;
    close: () => void;
    toggle: () => void;
    isOpen: () => boolean;
    destroy: () => void;
}

export interface MountMenuOptions {
    document: Document;
    container?: HTMLElement;
    toggle?: HTMLElement | null;
    initial?: Config;
    onCopyShareUrl?: () => void;
    onExportSvg?: () => void;
}

export function mountMenu(opts: MountMenuOptions): MenuHandle {
    const doc = opts.document;
    const container = opts.container ?? doc.body;
    const cfg = opts.initial ?? getConfig();

    const buildOpts: BuildMenuOptions = {};
    if (opts.onCopyShareUrl !== undefined) {
        buildOpts.onCopyShareUrl = opts.onCopyShareUrl;
    }
    if (opts.onExportSvg !== undefined) {
        buildOpts.onExportSvg = opts.onExportSvg;
    }
    const root = buildMenu(cfg, doc, buildOpts);
    container.appendChild(root);

    const open = (): void => {
        root.hidden = false;
    };
    const close = (): void => {
        root.hidden = true;
    };
    const toggle = (): void => {
        root.hidden = !root.hidden;
    };
    const isOpen = (): boolean => !root.hidden;

    const onBackdropClick = (e: MouseEvent): void => {
        if (e.target === root) {
            e.preventDefault();
            close();
        }
    };
    root.addEventListener('click', onBackdropClick);

    const onKeyDown = (e: KeyboardEvent): void => {
        if (e.key === 'Escape' && !root.hidden) {
            e.preventDefault();
            close();
        }
    };
    doc.addEventListener('keydown', onKeyDown);

    const toggleEl = opts.toggle ?? null;
    const onToggleClick = (e: MouseEvent): void => {
        e.preventDefault();
        toggle();
    };
    if (toggleEl !== null) {
        toggleEl.addEventListener('click', onToggleClick);
    }

    const unsubscribe = subscribe((next) => {
        syncMenu(root, next);
    });

    const destroy = (): void => {
        unsubscribe();
        doc.removeEventListener('keydown', onKeyDown);
        if (toggleEl !== null) {
            toggleEl.removeEventListener('click', onToggleClick);
        }
        root.removeEventListener('click', onBackdropClick);
        root.remove();
    };

    return { root, open, close, toggle, isOpen, destroy };
}
